import type { RelayGraphQLError } from '@confluence/relay-errors/entry-points/RelayError.ts';

import { isStatusCodeError } from '@confluence/error-types/entry-points/isStatusCodeError.ts';
import { ssrPrepareErrorsClient } from '@confluence/ssr-utilities/entry-points/SSRPrepareErrorsClient.ts';

// Mirrors GraphqlErrorProcessor's mark. Read through the global symbol registry rather than
// importing `isErrorMarkedAsHandled`, because ~20 test files mock
// `@confluence/graphql-error-processor` partially and a new import here would break all of them.
// The registry is the same mechanism GraphqlErrorProcessor relies on to share the mark across the
// preloader and main-app bundles, and `ignores errors already marked as handled` in this module's
// tests exercises the real `markErrorAsHandled`, so the key cannot drift unnoticed.
const HANDLED_ERROR_MARK_SYMBOL = Symbol.for('error marked as handled');

function isErrorMarkedAsHandled(error: unknown): boolean {
	return (
		typeof error === 'object' &&
		error !== null &&
		Boolean((error as Record<symbol, unknown>)[HANDLED_ERROR_MARK_SYMBOL])
	);
}

const isLegacyUnauthorizedGraphQLError = (error: Error | RelayGraphQLError) =>
	(error as RelayGraphQLError)?.extensions?.data?.authorized === false;

/**
 * A page the viewer is not allowed to see is a normal SSR outcome, not a defect — the render falls
 * back to the restricted-content screen. `query()` marks these as handled when a preloader resolves
 * with its query result, but a task that *rejects* with a permission error (or resolves with the
 * error itself) never passes through that path, so the handled mark alone does not keep restricted
 * pages out of the SSR prepare errors.
 *
 * Covers both status codes of the permission contract — 401 (unauthenticated) and 403 (not
 * permitted) — plus the legacy `extensions.data.authorized === false` shape, matching
 * `@confluence/unauthorized-error-boundary`. `isStatusCodeError` walks `graphQLErrors`,
 * `networkError` and `originalError`, so a rejected ApolloError wrapping a 403 is recognised too.
 * `@confluence/error-types` is used rather than the boundary's copy of this predicate to keep React
 * out of the preloader bundle.
 *
 * https://pug.jira-dev.com/wiki/spaces/CCB/pages/4709260932/GraphQL+Errors+Contract+for+Status+Code+401+and+403
 */
function isPermissionError(error: unknown): boolean {
	const candidate = error as Error | null | undefined;

	return (
		isStatusCodeError(candidate, '403', isLegacyUnauthorizedGraphQLError) ||
		isStatusCodeError(candidate, '401')
	);
}

/**
 * Errors that are an expected outcome of preloading and must not be reported as SSR prepare errors.
 */
function isExpectedPreloadError(error: unknown): boolean {
	return isErrorMarkedAsHandled(error) || isPermissionError(error);
}

function expandErrorToErrors(error: any): Error[] {
	// Error from createDataFetcherForSuspense might have nested errors
	const errors: Error[] = [error];
	if (Array.isArray(error.otherErrors)) {
		errors.push(...error.otherErrors);
	}
	delete error.otherErrors;
	return errors;
}

type PreloaderQueryResult = {
	hasErrors: boolean;
	errors?: ReadonlyArray<unknown>;
};

/**
 * `query()` and `queryAgg()` resolve to `{ data, errors, hasErrors }` rather than rejecting or
 * resolving to an `Error`, so a preloader that returns its result object straight into the task
 * list would otherwise slip past the `instanceof Error` checks below.
 */
function isPreloaderQueryResultWithErrors(value: unknown): value is PreloaderQueryResult {
	return (
		typeof value === 'object' &&
		value !== null &&
		(value as PreloaderQueryResult).hasErrors === true &&
		Array.isArray((value as PreloaderQueryResult).errors)
	);
}

/**
 * Only surfaces errors nobody has claimed. `query()` marks the expected ones (unauthorized,
 * not-found, and other known shapes) as handled before resolving, and those still come back with
 * `hasErrors: true` — reporting them here would turn every restricted or missing page into a bogus
 * SSR prepare error, since preloaders like `preloadPageStatusQuery` return their result object
 * straight into the task list.
 *
 * Filtering happens here, before the conversion below, because `new Error(message)` drops
 * `extensions` — the only place the permission status code lives on a plain GraphQL error object.
 */
function collectUnhandledQueryErrors(result: PreloaderQueryResult): Error[] {
	const unhandledErrors: Error[] = [];
	for (const error of result.errors ?? []) {
		if (isExpectedPreloadError(error)) {
			continue;
		}
		unhandledErrors.push(
			error instanceof Error
				? error
				: new Error(String((error as { message?: string })?.message ?? error)),
		);
	}
	return unhandledErrors;
}

function collectErrorsFromFulfilledValue(fulfilledValue: unknown): Error[] {
	if (fulfilledValue instanceof Error) {
		return expandErrorToErrors(fulfilledValue);
	}

	if (Array.isArray(fulfilledValue)) {
		return fulfilledValue.flatMap((value) =>
			value instanceof Error || isPreloaderQueryResultWithErrors(value)
				? collectErrorsFromFulfilledValue(value)
				: [],
		);
	}

	if (isPreloaderQueryResultWithErrors(fulfilledValue)) {
		return collectUnhandledQueryErrors(fulfilledValue);
	}

	return [];
}

/**
 * Wait for all preload tasks to settle.
 * @returns void if successful, or an array of errors if failed.
 */
export async function waitForAllReturnWhenError<T>(
	values: T | T[],
): Promise<void | Error[] | Awaited<T>[]> {
	if (!Array.isArray(values)) {
		values = [values];
	}
	return Promise.allSettled(values).then((results) => {
		const errors = [] as Error[];
		for (const result of results) {
			if (result.status === 'rejected') {
				errors.push(...expandErrorToErrors(result.reason));
			} else if (result.status === 'fulfilled') {
				errors.push(...collectErrorsFromFulfilledValue(result.value));
			}
		}

		// Drop the expected outcomes — already-handled and permission errors — so a restricted page
		// does not report a prepare error. Rejections and error values are filtered here rather than
		// at each collection site so that every path is held to the same rule; query results are
		// filtered earlier, while their `extensions` are still intact.
		const reportableErrors = errors.filter((error) => !isExpectedPreloadError(error));

		if (reportableErrors.length > 0) {
			// Errors on preloader functions are not meant to break anything.
			// But we still want to know them...
			for (const error of reportableErrors) {
				ssrPrepareErrorsClient.submitError(
					new Error(`Error caught by waitForAllReturnWhenError: ${error.message}`),
				);
			}

			return reportableErrors;
		}
	});
}
